/**
 * Document Year Utilities
 *
 * Derives which year a document belongs to and groups documents by year.
 * Used by DocumentsManager for year-based display and by folder structure
 * for Google Drive year subfolders.
 *
 * Year derivation priority:
 *   1. effectiveDate year (start of validity period)
 *   2. expiresAt year (end of validity period)
 *   3. null → "Documents" / permanent (no year subfolder in Drive)
 */

export type YearKey = number | 'documents';

interface DateableDocument {
  effectiveDate?: string;
  expiresAt?: string;
  uploadedAt?: string;
}

/**
 * Derive the year a document belongs to.
 * Returns null for undated/permanent documents — these are stored
 * directly in the Documents folder (no year subfolder).
 *
 * Only explicit date fields are used (effectiveDate, expiresAt).
 * uploadedAt is intentionally excluded so that documents without
 * a user-provided year are treated as permanent.
 */
export function getDocumentYear(doc: DateableDocument): number | null {
  // Use getUTCFullYear — dates are stored as "YYYY-01-01" (ISO/UTC).
  // getFullYear() in west-of-UTC timezones shifts "2025-01-01 00:00Z" to Dec 31 2024 local → wrong year.
  if (doc.effectiveDate) {
    const year = new Date(doc.effectiveDate).getUTCFullYear();
    if (!isNaN(year)) return year;
  }
  if (doc.expiresAt) {
    const year = new Date(doc.expiresAt).getUTCFullYear();
    if (!isNaN(year)) return year;
  }
  return null;
}

/**
 * Group documents by year.
 * Returns entries sorted newest-year-first, with 'documents' (undated) last.
 */
export function groupDocumentsByYear<T extends DateableDocument>(
  docs: T[]
): [YearKey, T[]][] {
  const groups = new Map<YearKey, T[]>();

  for (const doc of docs) {
    const year = getDocumentYear(doc);
    const key: YearKey = year ?? 'documents';

    const existing = groups.get(key);
    if (existing) {
      existing.push(doc);
    } else {
      groups.set(key, [doc]);
    }
  }

  // Sort: numeric years descending (newest first), 'documents' last
  const entries = Array.from(groups.entries());
  entries.sort((a, b) => {
    if (a[0] === 'documents') return 1;
    if (b[0] === 'documents') return -1;
    return (b[0] as number) - (a[0] as number);
  });

  return entries;
}
